'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import { ArrowLeft, FileText, Loader2, Calendar } from 'lucide-react';
import Newsletter from '@/components/Newsletter';
import { BlogCategoryDetailResponse, BlogPost } from '@/services/blogService';
import { blogService } from '@/services/blogService';
import BlogCard from '@/components/BlogCard';

interface CategoryDetailClientProps {
  data: BlogCategoryDetailResponse;
}

export default function CategoryDetailClient({ data }: CategoryDetailClientProps) {
  const { category: initialCategory, posts: initialPosts, pagination: initialPagination } = data;
  
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState(initialPagination);
  const [hasMorePosts, setHasMorePosts] = useState(initialPagination.page < initialPagination.pages);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMorePosts) return;

    setLoadingMore(true);
    setError(null);

    try {
      const nextPage = pagination.page + 1;
      const response = await blogService.getCategoryPosts(initialCategory.slug, nextPage, 12);
      
      setPosts(prevPosts => [...prevPosts, ...response.posts]);
      setPagination(response.pagination);
      setHasMorePosts(response.pagination.page < response.pagination.pages);
    } catch (err) {
      console.error('Error loading more posts:', err);
      setError('Failed to load more posts. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <Header variant="fixed" />

      {/* Category Header */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center space-x-2 text-sm text-gray-600">
                <li><Link href="/blog" className="hover:text-blue-600">Blog</Link></li>
                <li>/</li>
                <li><Link href="/blog/category" className="hover:text-blue-600">Categories</Link></li>
                <li>/</li>
                <li className="text-gray-900">{initialCategory.name}</li>
              </ol>
            </nav>

            {/* Back Button */}
            <div className="mb-6">
              <Link 
                href="/blog/category" 
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Categories
              </Link>
            </div>

            {/* Category Info */}
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {initialCategory.name}
              </h1>
              
              {initialCategory.description && (
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                  {initialCategory.description}
                </p>
              )}

              {/* Category Stats */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  {pagination.total} Articles
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Updated {formatDate(initialCategory.updatedAt)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Image */}
      {initialCategory.imageUrl && (
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
                <Image
                  src={initialCategory.imageUrl}
                  alt={initialCategory.name}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
                  priority
                  unoptimized
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {posts.length > 0 ? (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Articles in {initialCategory.name}
                  </h2>
                  <p className="text-gray-600">
                    Showing {posts.length} of {pagination.total} articles
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <BlogCard key={post.id} post={post} formatDate={formatDate} />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMorePosts && (
                  <div className="mt-12 text-center">
                    <button
                      onClick={loadMorePosts}
                      disabled={loadingMore}
                      className="inline-flex items-center px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Loading More Articles...
                        </>
                      ) : (
                        'Load More Articles'
                      )}
                    </button>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="mt-8 text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                      onClick={loadMorePosts}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Articles Found</h3>
                <p className="text-gray-600 mb-6">
                  There are no articles available in the {initialCategory.name} category yet.
                </p>
                <Link
                  href="/blog"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Browse All Articles
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <Newsletter />
    </div>
  );
}
